import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Divider,
  Chip,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardIcon from '@mui/icons-material/Keyboard';

interface KeyboardShortcutsHelpProps {
  open: boolean;
  onClose: () => void;
}

const shortcuts = [
  {
    section: 'Navigation',
    items: [
      { key: 'j / ↓', desc: 'Next article', highlight: true },
      { key: 'k / ↑', desc: 'Previous article', highlight: true },
      { key: 'g h', desc: 'Go to articles (home)' },
      { key: 'g s', desc: 'Go to subscriptions' },
      { key: 'g t', desc: 'Go to settings' },
    ],
  },
  {
    section: 'Reading',
    items: [
      { key: 'o', desc: 'Open selected article', highlight: true },
      { key: 'Enter', desc: 'Expand/collapse article details' },
      { key: 'Escape', desc: 'Close reader', highlight: true },
      { key: 'Space', desc: 'Scroll down in reader' },
    ],
  },
  {
    section: 'Actions',
    items: [
      { key: 's', desc: 'Toggle star/favorite', highlight: true },
      { key: 'm', desc: 'Toggle read/unread status' },
      { key: 'v', desc: 'Open original article in new tab' },
      { key: 't', desc: 'Translate current article (in reader)' },
      { key: 'Shift+A', desc: 'Mark all as read' },
    ],
  },
  {
    section: 'Reader Navigation',
    items: [
      { key: 'n / j', desc: 'Read next article' },
      { key: 'p / k', desc: 'Read previous article' },
    ],
  },
  {
    section: 'Other',
    items: [
      { key: '/', desc: 'Focus search', highlight: true },
      { key: '?', desc: 'Show/hide this help', highlight: true },
    ],
  },
];

const KeyboardShortcutsHelp: React.FC<KeyboardShortcutsHelpProps> = ({ open, onClose }) => {
  const theme = useTheme();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          pb: 1,
          background:
            theme.palette.mode === 'dark'
              ? 'linear-gradient(135deg, rgba(25, 118, 210, 0.1) 0%, rgba(156, 39, 176, 0.1) 100%)'
              : 'linear-gradient(135deg, rgba(25, 118, 210, 0.05) 0%, rgba(156, 39, 176, 0.05) 100%)',
        }}
      >
        <KeyboardIcon sx={{ mr: 1, color: 'primary.main' }} />
        <Typography variant="h6" sx={{ flexGrow: 1, fontSize: '1.1rem', fontWeight: 600 }}>
          Keyboard Shortcuts
        </Typography>
        <Chip label="?" size="small" sx={{ mr: 1, fontFamily: 'monospace', fontWeight: 600 }} />
        <IconButton size="small" onClick={onClose} aria-label="Close">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ py: 3, px: 3 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, fontStyle: 'italic' }}>
          Use keyboard shortcuts to quickly navigate and manage articles
        </Typography>

        {shortcuts.map((section, sectionIdx) => (
          <Box key={section.section}>
            {sectionIdx > 0 && <Divider sx={{ my: 2.5 }} />}
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="subtitle2"
                color="primary"
                sx={{
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  mb: 1.5,
                  fontSize: '0.8rem',
                }}
              >
                {section.section}
              </Typography>
              {section.items.map((item) => (
                <Box
                  key={item.key}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    py: 0.75,
                    px: 1.5,
                    borderRadius: 1,
                    transition: 'background-color 0.2s ease',
                    '&:hover': {
                      bgcolor: 'action.hover',
                    },
                    ...(item.highlight && {
                      bgcolor: 'action.selected',
                      '&:hover': {
                        bgcolor: 'action.selected',
                      },
                    }),
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: item.highlight ? 600 : 400,
                      color: item.highlight ? 'text.primary' : 'text.secondary',
                    }}
                  >
                    {item.desc}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                    {item.key.split(' / ').map((k, idx) => (
                      <React.Fragment key={k}>
                        {idx > 0 && (
                          <Typography
                            variant="caption"
                            color="text.disabled"
                            sx={{ mx: 0.25, fontWeight: 300 }}
                          >
                            or
                          </Typography>
                        )}
                        <Box
                          component="kbd"
                          sx={{
                            px: 1.25,
                            py: 0.5,
                            fontSize: '0.75rem',
                            fontFamily: 'monospace',
                            fontWeight: 600,
                            bgcolor:
                              theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'grey.100',
                            color: theme.palette.mode === 'dark' ? 'grey.300' : 'grey.800',
                            border: 1,
                            borderColor: 'divider',
                            borderRadius: 0.75,
                            minWidth: 28,
                            textAlign: 'center',
                            lineHeight: 1.4,
                            boxShadow:
                              theme.palette.mode === 'dark'
                                ? '0 1px 2px rgba(0,0,0,0.4)'
                                : '0 1px 2px rgba(0,0,0,0.1)',
                          }}
                        >
                          {k}
                        </Box>
                      </React.Fragment>
                    ))}
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        ))}

        <Box
          sx={{
            mt: 3,
            pt: 2,
            borderTop: 1,
            borderColor: 'divider',
            textAlign: 'center',
          }}
        >
          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            Tip: Shortcuts are automatically disabled when typing in input fields
          </Typography>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default KeyboardShortcutsHelp;
